import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import FeedbackButton from "./components/FeedbackButton";
import CookieConsent from "./components/CookieConsent";
import CanonicalTag from "./components/CanonicalTag";

const SearchPage = lazy(() => import("./pages/SearchPage"));
const PracticeDetails = lazy(() => import("./pages/PracticeDetails"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Disclaimers = lazy(() => import("./pages/Disclaimers"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ClaimListing = lazy(() => import("./pages/ClaimListing"));
const PracticeDashboard = lazy(() => import("./pages/PracticeDashboard"));
const PracticeLogin = lazy(() => import("./pages/PracticeLogin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const BoroughPage = lazy(() => import("./pages/BoroughPage"));
const ChainDashboard = lazy(() => import("./pages/ChainDashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const PrivatePractitioner = lazy(() => import("./pages/PrivatePractitioner"));
const PrivateLanding = lazy(() => import("./pages/PrivateLanding"));
const AdminPrivatePractitioners = lazy(() => import("./pages/AdminPrivatePractitioners"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CanonicalTag />
        <Suspense fallback={
          <div className="flex min-h-screen items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        }>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/practice/:id" element={<PracticeDetails />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/disclaimers" element={<Disclaimers />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/claim" element={<ClaimListing />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/practice-login" element={<PracticeLogin />} />
            <Route path="/dashboard" element={<PracticeDashboard />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/area/:borough" element={<BoroughPage />} />
            <Route path="/dashboard/chain/osbon" element={<ChainDashboard />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/private" element={<PrivateLanding />} />
            <Route path="/private/:slug" element={<PrivatePractitioner />} />
            <Route path="/admin/private-practitioners" element={<AdminPrivatePractitioners />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <FeedbackButton />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
